import config from './config';
import { authUtils } from './supabase';

const API_URL = config.apiBaseUrl;

// Утилитарная функция для создания заголовков с авторизацией
async function getHeaders(includeAuth: boolean = true): Promise<HeadersInit> {
  const headers: HeadersInit = {
    "Content-Type": "application/json"
  };

  if (includeAuth && config.authEnabled) {
    const token = await authUtils.getAccessToken();
    console.log('Auth token:', token ? 'present' : 'missing');
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      console.warn('No auth token available');
    }
  } else {
    console.log('Auth not enabled or not included');
  }

  // Add rt_clickid for attribution tracking if present in localStorage
  const rtClickId = localStorage.getItem('rt_clickid');
  if (rtClickId) {
    headers["X-RT-ClickID"] = rtClickId;
    console.log('📊 Adding rt_clickid to request:', rtClickId.substring(0, 20) + '...');
  }

  return headers;
}

// Утилитарная функция для выполнения API запросов с retry
export async function apiRequest(endpoint: string, options: RequestInit = {}, retryCount = 0): Promise<any> {
  // Автоматически добавляем префикс /api ко всем путям
  const apiEndpoint = endpoint.startsWith('/api/') ? endpoint : `/api${endpoint}`;
  const url = `${API_URL}${apiEndpoint}`;
  const maxRetries = 3;
  const retryDelay = 1000; // 1 секунда
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...await getHeaders(),
        ...options.headers
      }
    });

    if (!response.ok) {
      // Специальная обработка для ошибок авторизации
      if (response.status === 401 || response.status === 403) {
        console.warn('Authentication error, triggering global logout');
        window.dispatchEvent(new CustomEvent('auth:force-logout'));
      }
      
      const errorData = await response.json().catch(() => ({ detail: 'Network error' }));
      const error = new Error(errorData.detail || `HTTP ${response.status}`);
      (error as any).status = response.status;
      
      // Retry для временных ошибок
      if (retryCount < maxRetries && shouldRetry(response.status)) {
        console.warn(`API request failed with status ${response.status}, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
        return apiRequest(endpoint, options, retryCount + 1);
      }
      
      throw error;
    }

    return await response.json();
  } catch (error) {
    // Retry для сетевых ошибок
    if (retryCount < maxRetries && isNetworkError(error)) {
      console.warn(`Network error, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
      return apiRequest(endpoint, options, retryCount + 1);
    }
    
    console.error(`API request failed: ${endpoint}`, error);
    throw error;
  }
}

// Функция для определения, стоит ли повторять запрос
function shouldRetry(status: number): boolean {
  // Повторяем для временных ошибок
  return status === 499 || // Client disconnected
         status === 500 || // Internal server error
         status === 502 || // Bad gateway
         status === 503 || // Service unavailable
         status === 504;   // Gateway timeout
}

// Функция для определения сетевых ошибок
function isNetworkError(error: any): boolean {
  return error.name === 'TypeError' || // Network error
         error.message?.includes('fetch') ||
         error.message?.includes('network');
}

// Получение статуса авторизации
export async function getAuthStatus() {
  return apiRequest('/auth/status');
}

// Создание игры
export async function createGame(playerName?: string) {
  const result = await apiRequest('/games', {
    method: "POST",
    body: JSON.stringify({ player_name: playerName || "Игрок" })
  });

  // Трекинг: старт игры
  if (result && result.game_id && (window as any).adx) {
    // Google Ads конверсия
    (window as any).adx.adsConversion((window as any).adx.SENDTO.game_start, 0);

    // RedTrack S2S postback с gclid
    const clickParams = (window as any).adx.getClickParams?.() || {};
    (window as any).adx.trackEventS2S('game_start', {
      gameId: result.game_id,
      gclid: clickParams.gclid  // Передаём gclid для RedTrack аналитики
    });
  }

  return result;
}

// Бросок кубика
export async function rollDice(gameId: number) {
  const result = await apiRequest(`/games/${gameId}/roll`, {
    method: "POST"
  });

  // Получаем click params один раз для всех событий
  const clickParams = (window as any).adx?.getClickParams?.() || {};

  // Трекинг: первая 6ка (запрос принят)
  if (result && result.request_accepted === 1 && (window as any).adx) {
    // Google Ads конверсия
    (window as any).adx.adsConversion((window as any).adx.SENDTO.request_accepted, 0);

    // RedTrack S2S postback с gclid
    (window as any).adx.trackEventS2S('request_accepted', {
      gameId: gameId,
      gclid: clickParams.gclid  // Передаём gclid для RedTrack аналитики
    });
  }

  // Трекинг: завершение игры
  if (result && result.finished === 1 && (window as any).adx) {
    // Google Ads конверсия
    (window as any).adx.adsConversion((window as any).adx.SENDTO.game_finish, 0);

    // RedTrack S2S postback с gclid
    (window as any).adx.trackEventS2S('game_finish', {
      gameId: gameId,
      gclid: clickParams.gclid  // Передаём gclid для RedTrack аналитики
    });
  }

  return result;
}

// Получение истории чата
export async function getChatHistory(gameId: number) {
  return apiRequest(`/games/${gameId}/chat`);
}

// Отправка сообщения в чат
export async function sendChatMessage(gameId: number, role: string, content: string) {
  return apiRequest(`/games/${gameId}/chat`, {
    method: "POST",
    body: JSON.stringify({ role, content })
  });
}

// Получение профиля пользователя
export async function getMyProfile() {
  return apiRequest('/me');
}

// Получение списка игр пользователя
export async function getMyGames(page: number = 1, limit: number = 20) {
  return apiRequest(`/me/games?page=${page}&limit=${limit}`);
}

// Миграция старых игр
export async function migrateOldGames() {
  return apiRequest('/migrate-old-games', {
    method: "POST"
  });
}

// Получение данных игры
export async function getGameData(gameId: number) {
  return apiRequest(`/games/${gameId}`);
}

// Проверка доступа к созданию игр
export async function checkGameAccess() {
  return apiRequest('/me/payment-access');
}

// Telegram авторизация
export async function verifyTelegramAuth(telegramData: any, guest_id?: string) {
  console.log('🔵 verifyTelegramAuth called with data:', telegramData, 'guest_id:', guest_id);
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    // Передаём guest_id в header если он есть
    if (guest_id) {
      headers['X-Guest-ID'] = guest_id;
      console.log('📨 Sending guest_id in header:', guest_id);
    }

    const result = await apiRequest('/auth/telegram/verify', {
      method: 'POST',
      headers,
      body: JSON.stringify(telegramData)
    });
    console.log('✅ verifyTelegramAuth success:', result);
    return result;
  } catch (error) {
    console.error('❌ verifyTelegramAuth error:', error);
    throw error;
  }
}

export async function linkTelegramAccount(telegramData: any, userId: number) {
  return apiRequest('/auth/telegram/link', {
    method: 'POST',
    body: JSON.stringify({
      telegram_data: telegramData,
      user_id: userId
    })
  });
}

export async function getTelegramAuthStatus() {
  return apiRequest('/auth/telegram/status');
}

// Attribution tracking
export async function saveAttribution(rtClickId: string, source: string = 'redtrack'): Promise<any> {
  try {
    console.log('📊 Saving attribution:', rtClickId.substring(0, 10), source);
    const response = await apiRequest('/attribution/save-attribution', {
      method: 'POST',
      body: JSON.stringify({
        rt_clickid: rtClickId,
        source: source
      })
    });
    console.log('✅ Attribution saved successfully');
    return response;
  } catch (error) {
    console.error('❌ Failed to save attribution:', error);
    // Не бросаем ошибку - attribution не должна ломать основной flow
    return { success: false, error };
  }
}

export async function getMyAttribution(): Promise<any> {
  return apiRequest('/attribution/my-attribution');
}

// ========================================
// GUEST MODE API FUNCTIONS
// ========================================

// Утилита для guest mode API запросов с header X-Guest-Mode: true
async function guestApiRequest(endpoint: string, options: RequestInit = {}, retryCount = 0): Promise<any> {
  const apiEndpoint = endpoint.startsWith('/api/') ? endpoint : `/api${endpoint}`;
  const url = `${API_URL}${apiEndpoint}`;
  const maxRetries = 3;
  const retryDelay = 1000;

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-Guest-Mode": "true",  // Ключевой header для guest mode
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Network error' }));
      const error = new Error(errorData.detail || `HTTP ${response.status}`);
      (error as any).status = response.status;
      (error as any).data = errorData;  // Сохраняем полные данные ошибки (для 401 с guest_limit_reached)

      // Retry для временных ошибок (но НЕ для 401 - это нормальная блокировка гостя)
      if (response.status !== 401 && retryCount < maxRetries && shouldRetry(response.status)) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
        return guestApiRequest(endpoint, options, retryCount + 1);
      }

      throw error;
    }

    return await response.json();
  } catch (error: any) {
    // Retry для сетевых ошибок
    if (retryCount < maxRetries && isNetworkError(error)) {
      await new Promise(resolve => setTimeout(resolve, retryDelay * (retryCount + 1)));
      return guestApiRequest(endpoint, options, retryCount + 1);
    }

    console.error(`Guest API request failed: ${endpoint}`, error);
    throw error;
  }
}

// Создание гостевой игры
export async function createGuestGame(playerName: string = "Гость") {
  const result = await guestApiRequest('/games', {
    method: "POST",
    body: JSON.stringify({ player_name: playerName })
  });

  // Трекинг: старт гостевой игры
  if (result && result.game_id && (window as any).adx) {
    (window as any).adx.adsConversion((window as any).adx.SENDTO.game_start, 0);
    (window as any).adx.trackEventS2S('guest_game_start', {
      gameId: result.game_id,
      isGuest: true
      // gclid добавляется автоматически через ...getClickParams() в trackEventS2S
    });
  }

  return result;
}

// Связывание гостевых игр с авторизованным пользователем
export async function linkGuestGames(guest_id: string) {
  return apiRequest('/link-guest-games', {
    method: 'POST',
    body: JSON.stringify({ guest_id })
  });
}

// Бросок кубика для гостевой игры
export async function rollDiceGuest(gameId: number) {
  const result = await guestApiRequest(`/games/${gameId}/roll`, {
    method: "POST"
  });

  // Трекинг для гостевых игр
  if (result && result.request_accepted === 1 && (window as any).adx) {
    (window as any).adx.adsConversion((window as any).adx.SENDTO.request_accepted, 0);
    (window as any).adx.trackEventS2S('guest_request_accepted', {
      gameId: gameId,
      isGuest: true
      // gclid добавляется автоматически через ...getClickParams() в trackEventS2S
    });
  }

  if (result && result.finished === 1 && (window as any).adx) {
    (window as any).adx.adsConversion((window as any).adx.SENDTO.game_finish, 0);
    (window as any).adx.trackEventS2S('guest_game_finish', {
      gameId: gameId,
      isGuest: true
      // gclid добавляется автоматически через ...getClickParams() в trackEventS2S
    });
  }

  return result;
}

// Получение истории чата для гостевой игры
export async function getChatHistoryGuest(gameId: number) {
  return guestApiRequest(`/games/${gameId}/chat`);
}

// Отправка сообщения в чат для гостевой игры
export async function postChatMessageGuest(gameId: number, role: string, content: string) {
  return guestApiRequest(`/games/${gameId}/chat`, {
    method: "POST",
    body: JSON.stringify({ role, content })
  });
}
